import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FolderOpen,
  Folder as FolderIcon,
  Inbox,
  Bell,
  Building2,
  ClipboardList,
  Settings,
  Home,
  ChevronDown,
  ChevronRight,
  X,
  Users,
  Activity,
  Search,
  Pin,
  PinOff,
  Star,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTourStore } from '@/stores/tour';
import { useUIStore } from '@/stores/ui';
import { cn } from '@/lib/cn';
import type { Role } from '@/types/api';
import { BillerBayLogo } from '@/components/ui/BillerBayLogo';
import { useFolderFavorites, useRemoveFolderFavorite } from '@/api/favorites';
import { useOrganizations } from '@/api/organizations';
import { useWorkspaces } from '@/api/workspaces';
import { useFolders } from '@/api/folders';
import { SidebarFolderTree } from '@/components/folders/SidebarFolderTree';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

function buildNav(role: Role | undefined, orgId: string | null): NavItem[] {
  const isStaff = role === 'sysadmin' || role === 'manager' || role === 'staff';
  const isClient = role === 'client_admin' || role === 'client_user';
  const isAdmin = role === 'sysadmin' || role === 'manager';
  const items: NavItem[] = [];

  if (isClient) {
    items.push({ to: '/client', label: 'Home', icon: <Home size={16} /> });
    items.push({ to: '/requests', label: 'Requests', icon: <ClipboardList size={16} /> });
    if (role === 'client_admin' && orgId) {
      items.push({ to: `/workspace/${orgId}/team`, label: 'Team', icon: <Users size={16} /> });
      items.push({ to: `/workspace/${orgId}/activity`, label: 'Activity', icon: <Activity size={16} /> });
    }
  } else {
    items.push({
      to: isStaff ? '/staff' : '/client',
      label: 'Dashboard',
      icon: <LayoutDashboard size={16} />,
    });
    if (orgId && !isAdmin) {
      items.push({ to: `/workspace/${orgId}`, label: 'Workspace', icon: <FolderOpen size={16} /> });
    }
    items.push({ to: '/requests', label: 'Requests', icon: <Inbox size={16} /> });
    items.push({ to: '/search', label: 'Search', icon: <Search size={16} /> });
    items.push({ to: '/notifications', label: 'Notifications', icon: <Bell size={16} /> });
    if (isStaff) {
      items.push({ to: '/admin/organizations', label: 'Organizations', icon: <Building2 size={16} /> });
    }
    if (isAdmin) {
      items.push({ to: '/admin/settings', label: 'Settings', icon: <Settings size={16} /> });
    }
  }

  return items;
}

function OrgQuickNav({
  orgs,
  userId,
  onNavigate,
}: {
  orgs: Array<{ id: string; name: string; status: string }>;
  userId: string;
  onNavigate?: () => void;
}) {
  const storageKey = `bb_pinned_orgs_${userId}`;
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(storageKey) || '[]') as string[];
    } catch {
      return [];
    }
  });
  const [expanded, setExpanded] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const location = useLocation();

  const savePinned = (ids: string[]) => {
    setPinnedIds(ids);
    try { localStorage.setItem(storageKey, JSON.stringify(ids)); } catch { /* ignore */ }
  };

  const togglePin = (id: string) => {
    if (pinnedIds.includes(id)) {
      savePinned(pinnedIds.filter((p) => p !== id));
    } else {
      savePinned([...pinnedIds, id]);
    }
  };

  const active = orgs.filter((o) => o.status === 'active');
  const pinned = active.filter((o) => pinnedIds.includes(o.id));
  const rest = active.filter((o) => !pinnedIds.includes(o.id));
  const VISIBLE = 12;
  // The currently-open client must always stay visible — even if it sorts beyond
  // the truncation point. Otherwise selecting a client past #12 made it vanish
  // behind "+N more" (looked like the selected workspace collapsed).
  const activeOrgId = location.pathname.match(/\/workspace\/([^/]+)/)?.[1] ?? null;
  let visibleRest = showAll ? rest : rest.slice(0, VISIBLE);
  if (!showAll && activeOrgId && !visibleRest.some((o) => o.id === activeOrgId)) {
    const activeOrg = rest.find((o) => o.id === activeOrgId);
    if (activeOrg) visibleRest = [...visibleRest, activeOrg];
  }

  const OrgItem = ({ org, isPinned }: { org: { id: string; name: string }; isPinned: boolean }) => (
    <li className="group relative">
      <NavLink
        to={`/workspace/${org.id}`}
        onClick={onNavigate}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2.5 px-3 py-1.5 pr-8 text-[13px] font-medium transition-colors rounded-r-[6px] border-l-[3px]',
            isActive
              ? 'bg-[#EFF0FF] dark:bg-[#1E2040] text-[#0410BD] dark:text-[#818CF8] border-[#0410BD] dark:border-[#818CF8]'
              : 'text-[#12122C] dark:text-[#C4C4DC] hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] border-transparent',
          )
        }
      >
        <Building2 size={13} className="shrink-0 text-[#BABACE]" />
        <span className="truncate">{org.name}</span>
      </NavLink>
      <button
        type="button"
        title={isPinned ? 'Unpin' : 'Pin'}
        onClick={() => togglePin(org.id)}
        className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-0.5 text-[#BABACE] hover:text-[#676687] transition-opacity"
      >
        {isPinned ? <PinOff size={11} /> : <Pin size={11} />}
      </button>
    </li>
  );

  return (
    <div className="mt-4">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center justify-between w-full px-3 mb-1 text-[10px] font-semibold uppercase tracking-wider text-[#BABACE] hover:text-[#676687] transition-colors"
      >
        <span>Clients</span>
        {expanded ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
      </button>
      {expanded && (
        <ul className="flex flex-col gap-0.5">
          {pinned.length > 0 && (
            <>
              <li className="px-3 pt-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#BABACE]">
                Pinned
              </li>
              {pinned.map((org) => (
                <OrgItem key={org.id} org={org} isPinned={true} />
              ))}
              {rest.length > 0 && (
                <li className="px-3 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-[#BABACE]">
                  All clients
                </li>
              )}
            </>
          )}
          {visibleRest.map((org) => (
            <OrgItem key={org.id} org={org} isPinned={false} />
          ))}
          {!showAll && rest.length > VISIBLE && (
            <li>
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="w-full text-left px-3 py-1 text-[11px] text-[#BABACE] hover:text-[#676687]"
              >
                +{rest.length - VISIBLE} more
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function tourKeyFor(label: string): string {
  return `sidebar-${label.toLowerCase().replace(/\s+/g, '-')}`;
}

const activeClass =
  'bg-[#EFF0FF] dark:bg-[#1E2040] text-[#0410BD] dark:text-[#818CF8] border-l-[3px] border-[#0410BD] dark:border-[#818CF8] pl-[calc(0.75rem-3px)]';
const inactiveClass =
  'text-[#12122C] dark:text-[#C4C4DC] hover:bg-[#F2F2F8] dark:hover:bg-[#2A2A4A] border-l-[3px] border-transparent pl-[calc(0.75rem-3px)]';

function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/admin'}
      onClick={onNavigate}
      data-tour={tourKeyFor(item.label)}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors rounded-r-[6px]',
          isActive ? activeClass : inactiveClass,
        )
      }
    >
      <span className="shrink-0">{item.icon}</span>
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

function SidebarFooter({ fullName, email, isAdmin }: { fullName?: string; email?: string; isAdmin?: boolean }) {
  const startTour = useTourStore((s) => s.startTour);
  const startAdminTour = useTourStore((s) => s.startAdminTour);

  return (
    <div className="shrink-0 border-t border-[#E3E3F1] dark:border-[#2A2A4A] px-4 py-3 text-xs">
      <div className="truncate font-medium text-[#12122C] dark:text-[#C4C4DC]">{fullName}</div>
      <div className="truncate text-[#676687] dark:text-[#8888AA]">{email}</div>
      <button
        type="button"
        onClick={startTour}
        className="text-[11px] text-[#BABACE] hover:text-[#676687] dark:hover:text-[#8888AA] mt-1 block transition-colors"
      >
        Restart guided tour
      </button>
      {isAdmin && (
        <button
          type="button"
          onClick={startAdminTour}
          className="text-[11px] text-[#BABACE] hover:text-[#676687] dark:hover:text-[#8888AA] mt-0.5 block transition-colors"
        >
          Restart admin tour
        </button>
      )}
    </div>
  );
}

const PINNED_VISIBLE = 8;

export function Sidebar() {
  const { user } = useAuth();
  const favorites = useFolderFavorites();

  const { activeOrgId } = useAuth();
  const isClient = user?.role === 'client_admin' || user?.role === 'client_user';
  const isStaff = user?.role === 'sysadmin' || user?.role === 'manager' || user?.role === 'staff';

  const clientOrgId = isClient ? (activeOrgId ?? user?.organization_id ?? null) : null;
  const clientWorkspaces = useWorkspaces(clientOrgId ?? '');
  const clientWorkspaceId = clientWorkspaces.data?.[0]?.id ?? null;
  const clientFolders = useFolders(clientWorkspaceId);

  const orgsQuery = useOrganizations();

  const navItems = buildNav(user?.role, activeOrgId ?? user?.organization_id ?? null);

  const removeFavorite = useRemoveFolderFavorite();

  const favoriteList = favorites.data ?? [];
  const visibleFavorites = favoriteList.slice(0, PINNED_VISIBLE);
  const hiddenCount = favoriteList.length - visibleFavorites.length;
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const setSidebarOpen = useUIStore((s) => s.setSidebarOpen);

  const handleNavigate = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  return (
    <>
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col bg-[#12122C] w-64 shrink-0 overflow-hidden transition-transform duration-200',
          'md:relative md:inset-auto md:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        {/* Dark logo header */}
        <div className="flex items-center justify-between gap-2 px-4 h-14 border-b border-white/10 shrink-0">
          <BillerBayLogo variant="light" height={22} />
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            className="md:hidden p-1 rounded text-white/60 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* White nav area */}
        <div className="flex flex-col flex-1 bg-white dark:bg-[#16162A] border-r border-[#E3E3F1] dark:border-[#2A2A4A] overflow-hidden">
          <nav className="flex-1 overflow-y-auto custom-scrollbar py-3 px-2">
            {/* Pinned folder favorites — grouped by organization */}
            {visibleFavorites.length > 0 && (
              <>
                <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#BABACE]">
                  Pinned
                </div>
                <div className="flex flex-col mb-4">
                  {(() => {
                    // Group favorites by org, preserving pin order within each group
                    const groups = new Map<string, { orgName: string; items: typeof visibleFavorites }>();
                    for (const fav of visibleFavorites) {
                      const key = fav.organization_id;
                      if (!groups.has(key)) groups.set(key, { orgName: fav.organization_name, items: [] });
                      groups.get(key)!.items.push(fav);
                    }
                    return Array.from(groups.entries()).map(([orgId, { orgName, items }]) => (
                      <div key={orgId} className="mb-2">
                        <NavLink
                          to={`/workspace/${orgId}`}
                          onClick={handleNavigate}
                          className="flex items-center gap-1.5 px-3 pt-1.5 pb-0.5 group"
                        >
                          <Building2 size={11} className="shrink-0 text-[#BABACE] group-hover:text-[#676687] transition-colors" />
                          <span className="truncate text-[10px] font-semibold uppercase tracking-wider text-[#BABACE] group-hover:text-[#676687] transition-colors">
                            {orgName}
                          </span>
                        </NavLink>
                        <ul className="flex flex-col gap-0.5 pl-2">
                          {items.map((fav) => (
                            <li key={fav.id} className="group relative">
                              <NavLink
                                to={`/workspace/${fav.organization_id}/folders/${fav.folder_id}`}
                                onClick={handleNavigate}
                                className={({ isActive }) =>
                                  cn(
                                    'flex items-center gap-2.5 px-3 py-1.5 pr-8 text-sm font-medium transition-colors rounded-r-[6px]',
                                    isActive ? activeClass : inactiveClass,
                                  )
                                }
                                title={fav.folder_name}
                              >
                                <FolderIcon size={14} className="shrink-0 text-[#676687] dark:text-[#8888AA]" />
                                <span className="truncate">{fav.folder_name}</span>
                              </NavLink>
                              <button
                                type="button"
                                title="Unpin folder"
                                onClick={() => removeFavorite.mutate(fav.folder_id)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-0.5 text-[#BABACE] hover:text-amber-500 transition-all"
                              >
                                <Star size={12} className="fill-current" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ));
                  })()}
                  {hiddenCount > 0 && (
                    <span className="block px-3 py-1 text-[11px] text-[#BABACE]">
                      +{hiddenCount} more pinned
                    </span>
                  )}
                </div>
              </>
            )}

            <ul className="flex flex-col gap-0.5">
              {navItems.map((item) => (
                <li key={item.to}>
                  <NavItemLink item={item} onNavigate={handleNavigate} />
                </li>
              ))}
            </ul>

            {/* Client: folder tree */}
            {isClient && clientOrgId && (
              <div className="mt-3">
                <div className="mb-1 px-3 text-[10px] font-semibold uppercase tracking-wider text-[#BABACE]">
                  Folders
                </div>
                {clientFolders.isLoading ? (
                  <p className="px-3 py-1 text-[11px] text-[#BABACE]">Loading…</p>
                ) : (
                  <SidebarFolderTree
                    orgId={clientOrgId}
                    folders={clientFolders.data ?? []}
                    onNavigate={handleNavigate}
                  />
                )}
              </div>
            )}

            {/* Staff: pinnable org quick-nav */}
            {isStaff && (orgsQuery.data?.length ?? 0) > 0 && user?.id && (
              <OrgQuickNav
                orgs={orgsQuery.data ?? []}
                userId={user.id}
                onNavigate={handleNavigate}
              />
            )}

          </nav>

          <SidebarFooter
            fullName={user?.full_name}
            email={user?.email}
            isAdmin={user?.role === 'sysadmin' || user?.role === 'manager'}
          />
        </div>
      </aside>
    </>
  );
}
